import type { Vec2 } from '../core/math';
import { wrapIndex } from '../core/math';

/**
 * A uniform bucket grid over the centerline, so "what else is near this point"
 * costs a handful of cells rather than a scan of the whole lap.
 *
 * Both things that have to be kept off the circuit use it — the barrier, which
 * pinches in wherever the full run-off would reach another part of the lap,
 * and the landmarks, which are pushed out until their footprint is clear of
 * all of it. Each asks a different question of the same structure, so the
 * structure is shared and the questions are not.
 */
export class PointGrid {
  private readonly cells = new Map<number, number[]>();
  private readonly cols: number;
  private readonly minX: number;
  private readonly minY: number;

  constructor(
    private readonly pts: readonly Vec2[],
    private readonly cell: number,
  ) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    for (const p of pts) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
    }
    this.minX = minX - cell;
    this.minY = minY - cell;
    this.cols = Math.ceil((maxX - minX) / cell) + 3;
    for (let i = 0; i < pts.length; i++) {
      const k = this.key(pts[i].x, pts[i].y);
      const bucket = this.cells.get(k);
      if (bucket) bucket.push(i);
      else this.cells.set(k, [i]);
    }
  }

  private key(x: number, y: number): number {
    return Math.floor((y - this.minY) / this.cell) * this.cols + Math.floor((x - this.minX) / this.cell);
  }

  /**
   * True when nothing within `radius` of the point belongs to a stretch more
   * than `skip` samples away around the ring — i.e. the only thing this close
   * is the road the caller is already standing beside.
   */
  clearOfOthers(x: number, y: number, radius: number, origin: number, skip: number, n: number): boolean {
    const gx = Math.floor((x - this.minX) / this.cell);
    const gy = Math.floor((y - this.minY) / this.cell);
    const reach = Math.ceil(radius / this.cell);
    const r2 = radius * radius;
    for (let dy = -reach; dy <= reach; dy++) {
      for (let dx = -reach; dx <= reach; dx++) {
        const bucket = this.cells.get((gy + dy) * this.cols + (gx + dx));
        if (!bucket) continue;
        for (const j of bucket) {
          const gap = Math.min(wrapIndex(j - origin, n), wrapIndex(origin - j, n));
          if (gap <= skip) continue;
          const p = this.pts[j];
          if ((p.x - x) ** 2 + (p.y - y) ** 2 < r2) return false;
        }
      }
    }
    return true;
  }

  /**
   * Distance to the nearest point, capped: anything further away than
   * `limit` returns `limit`, since callers only ever ask "is there at least
   * this much room" and searching past that is wasted work.
   */
  nearestWithin(x: number, y: number, limit: number): number {
    const gx = Math.floor((x - this.minX) / this.cell);
    const gy = Math.floor((y - this.minY) / this.cell);
    const reach = Math.ceil(limit / this.cell);
    let best = limit * limit;
    for (let dy = -reach; dy <= reach; dy++) {
      for (let dx = -reach; dx <= reach; dx++) {
        const bucket = this.cells.get((gy + dy) * this.cols + (gx + dx));
        if (!bucket) continue;
        for (const j of bucket) {
          const p = this.pts[j];
          const d2 = (p.x - x) ** 2 + (p.y - y) ** 2;
          if (d2 < best) best = d2;
        }
      }
    }
    return Math.sqrt(best);
  }
}
